/**
 * Leitura e auto-mapeamento de planilhas de contatos (CSV/XLSX).
 *
 * Extraído do ContactImportWizard para a importação da fila de prospecção
 * usar as MESMAS heurísticas: mesma planilha tem que dar o mesmo resultado
 * entrando por qualquer porta.
 */
import * as XLSX from "xlsx";

// ─── Heuristics for auto-mapping ─────────────────────────────────────────────

export const COLUMN_ALIASES: Record<string, string[]> = {
  phoneNumber: ["telefone", "phone", "whatsapp", "celular", "cel", "numero", "fone", "tel", "mobile", "telefone1", "telefone 1", "whats"],
  name: ["nome", "name", "primeiro nome", "first name", "firstname", "primeironome", "nome completo"],
  lastName: ["sobrenome", "last name", "lastname", "surname", "ultimo nome", "ultimosobrenome", "sobre nome"],
  email: ["email", "e-mail", "mail", "correio", "email corporativo"],
  contactType: ["tipo_contato", "tipo contato", "tipo", "type", "contact type"],
  companyName: ["orgao_empresa", "empresa", "orgao", "company", "organizacao", "org", "instituicao", "razao social", "razaosocial", "orgao empresa"],
  companyType: ["tipo_orgao", "tipo orgao", "tipo empresa", "segmento", "ramo", "company type"],
  sector: ["setor", "sector", "secretaria", "departamento", "dept", "area"],
  jobTitle: ["cargo", "funcao", "funcao/cargo", "job title", "jobtitle", "position", "posicao", "titulo", "role"],
  city: ["cidade", "city", "municipio"],
  state: ["estado", "state", "uf"],
  notes: ["observacoes", "notes", "obs", "nota", "observacao", "comentarios", "comments"],
};

export function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_\-\.]/g, " ")
    .replace(/\s+/g, " ");
}

export function autoDetectMapping(header: string): string {
  const normalized = normalizeHeader(header);
  for (const [fieldKey, aliases] of Object.entries(COLUMN_ALIASES)) {
    for (const alias of aliases) {
      if (normalized === normalizeHeader(alias)) return fieldKey;
    }
  }
  for (const [fieldKey, aliases] of Object.entries(COLUMN_ALIASES)) {
    for (const alias of aliases) {
      if (normalized.includes(normalizeHeader(alias)) || normalizeHeader(alias).includes(normalized)) {
        return fieldKey;
      }
    }
  }
  return "__ignore__";
}

export function detectByContent(rows: Record<string, string>[], header: string): string {
  const sample = rows.slice(0, 20).map(r => (r[header] ?? "").trim()).filter(Boolean);
  if (sample.length === 0) return "__ignore__";

  const emailCount = sample.filter(v => v.includes("@") && v.includes(".")).length;
  if (emailCount > sample.length * 0.5) return "email";

  const phoneCount = sample.filter(v => {
    const digits = v.replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 15;
  }).length;
  if (phoneCount > sample.length * 0.5) return "phoneNumber";

  const ufCount = sample.filter(v => /^[A-Z]{2}$/.test(v.trim().toUpperCase())).length;
  if (ufCount > sample.length * 0.6) return "state";

  const nameCount = sample.filter(v => /^[A-Za-zÀ-ÿ\s]+$/.test(v) && v.includes(" ")).length;
  if (nameCount > sample.length * 0.5) return "name";

  return "__ignore__";
}


export type ParsedSheet = { headers: string[]; rows: Record<string, string>[] };

/** Lê o arquivo e devolve cabeçalhos + linhas (strings, sem linhas vazias). */
export function parseContactFile(file: File): Promise<ParsedSheet> {
  const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo"));
    reader.onload = (ev) => {
      let headers: string[] = [];
      let rows: Record<string, string>[] = [];
      try {
        if (isExcel) {
          const data = new Uint8Array(ev.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonRows: Record<string, string>[] = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
          if (jsonRows.length > 0) {
            headers = Object.keys(jsonRows[0]);
            rows = jsonRows.map((row) =>
              Object.fromEntries(Object.entries(row).map(([k, v]) => [k, String(v).trim()])),
            );
          }
        } else {
          const text = ev.target?.result as string;
          const lines = text.split(/\r?\n/).filter((l) => l.trim());
          if (lines.length > 0) {
            const sep = lines[0].includes(";") ? ";" : ",";
            headers = lines[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, ""));
            rows = lines.slice(1).map((line) => {
              const vals = line.split(sep).map((v) => v.trim().replace(/^"|"$/g, ""));
              return headers.reduce((obj, h, i) => ({ ...obj, [h]: vals[i] ?? "" }), {} as Record<string, string>);
            });
          }
        }
      } catch (e) {
        reject(e);
        return;
      }
      rows = rows.filter((r) => Object.values(r).some((v) => v.trim() !== ""));
      resolve({ headers, rows });
    };
    if (isExcel) reader.readAsArrayBuffer(file);
    else reader.readAsText(file, "UTF-8");
  });
}

/**
 * Monta a linha a enviar a partir do mapeamento. Colunas repetidas para o
 * mesmo campo: vale a primeira preenchida, na ordem da planilha.
 */
export function buildRow(
  raw: Record<string, string>,
  mapping: Record<string, string>,
  headers: string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const col of headers) {
    const field = mapping[col];
    if (!field || field === "__ignore__") continue;
    const v = (raw[col] ?? "").trim();
    if (!v || out[field]) continue;
    out[field] = field === "state" ? v.slice(0, 2).toUpperCase() : v;
  }
  return out;
}

/** Quão forte foi o reconhecimento: nome exato > nome parecido > só pelo conteúdo. */
const NEVER_MAP = ["cpf", "cnpj", "rg", "utm", "user agent", "referrer", "id"];

export function detectWithConfidence(
  header: string,
  rows: Record<string, string>[],
): { target: string; score: number } {
  const normalized = normalizeHeader(header);
  // Documento, UTM e afins nunca são contato — nem quando o conteúdo parece telefone.
  if (NEVER_MAP.some((k) => normalized === k || normalized.startsWith(k + " ") || normalized.endsWith(" " + k))) {
    return { target: "__ignore__", score: 0 };
  }
  for (const [fieldKey, aliases] of Object.entries(COLUMN_ALIASES)) {
    if (aliases.some((a) => normalized === normalizeHeader(a))) return { target: fieldKey, score: 3 };
  }
  const partial = autoDetectMapping(header);
  if (partial !== "__ignore__") return { target: partial, score: 2 };
  const byContent = detectByContent(rows, header);
  return { target: byContent, score: byContent === "__ignore__" ? 0 : 1 };
}

/**
 * Cabeçalho → campo do sistema, sem repetir campo.
 *
 * Quando duas colunas disputam o mesmo campo, ganha a que foi reconhecida
 * pelo NOME ("Estado" é estado; "UTM_Source" com "FB"/"IG" não é), e em
 * empate a mais preenchida ("Telefone" vazio perde para "Telefone_1").
 */
/**
 * Campos que podem vir de mais de uma coluna: na importação, cada linha usa a
 * primeira coluna preenchida ("Telefone" vazio → pega "Telefone_1").
 */
export const MULTI_COLUMN_FIELDS = new Set(["phoneNumber", "email"]);

export function autoMapColumns(headers: string[], rows: Record<string, string>[]): Record<string, string> {
  const fill = (h: string) => rows.filter((r) => (r[h] ?? "").trim() !== "").length;
  const owner = new Map<string, { header: string; score: number }>();
  const map: Record<string, string> = {};
  for (const header of headers) {
    const { target, score } = detectWithConfidence(header, rows);
    map[header] = "__ignore__";
    if (target === "__ignore__") continue;
    // Telefone/Telefone_1 ficam as duas; quem lê a linha escolhe a preenchida.
    if (MULTI_COLUMN_FIELDS.has(target) && score >= 2) {
      // Uma coluna reconhecida pelo nome desbanca a que veio só pelo conteúdo (CPF).
      const weak = owner.get(target);
      if (weak && weak.score < 2) map[weak.header] = "__ignore__";
      owner.set(target, { header, score });
      map[header] = target;
      continue;
    }
    const current = owner.get(target);
    if (current && MULTI_COLUMN_FIELDS.has(target) && current.score >= 2) continue;
    if (current) {
      const keepCurrent =
        current.score > score || (current.score === score && fill(current.header) >= fill(header));
      if (keepCurrent) continue;
      map[current.header] = "__ignore__";
    }
    owner.set(target, { header, score });
    map[header] = target;
  }
  return map;
}

/**
 * Junta a sugestão da IA com a heurística: a IA manda, mas campo que ela
 * deixou vazio e a heurística reconheceu pelo NOME da coluna entra — "Telefone"
 * escrito no cabeçalho não pode virar "ignorar" por capricho do modelo.
 * Para telefone/e-mail, a segunda coluna também entra (fallback por linha).
 */
export function mergeWithHeuristic(
  ai: Record<string, string>,
  heuristic: Record<string, string>,
  headers: string[],
  rows: Record<string, string>[],
): Record<string, string> {
  const out = { ...ai };
  const usedByAi = new Set(Object.values(ai).filter((f) => f !== "__ignore__"));
  for (const header of headers) {
    const field = heuristic[header];
    if (!field || field === "__ignore__") continue;
    if (usedByAi.has(field) && !MULTI_COLUMN_FIELDS.has(field)) continue;
    if ((out[header] ?? "__ignore__") !== "__ignore__") continue;
    if (detectWithConfidence(header, rows).score < 3) continue;
    out[header] = field;
    usedByAi.add(field);
  }
  return out;
}
